import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, TextField, TextAreaField, Toggle, normalizeOptions } from './fields';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { findInvalidEmails } from '../../utils/email';
import {
  ChassisOverride, ScacFormOptions, createChassisOverride, getChassisOverrides,
  getScacFormOptions, updateChassisOverride,
} from '../../services/scacprofiles.service';

/**
 * Bespoke edit/create drawer for SCAC profiles (cms.scacprofiles, "SCACs"). Text fields +
 * Y/N toggles, Provider/Pool selects (from /scacprofiles/form-options), readonly Bill To
 * cross-reference + trace datetimes, a full-width Comments textarea, and an inline
 * chassis-override sub-grid (cms.scacprofiles_chassisoverride, keyed by the parent recno).
 *
 * Overrides are STAGED: add/edit/remove rows in the drawer, then on save the parent is
 * persisted first (create → recno, or update) and the overrides reconcile against it
 * (new → POST, changed → PUT, removed → PUT deleted='Y'). A new SCAC can stage overrides
 * before its first save. Reversed soft-delete on `active` (drawer shows an Active toggle).
 *
 * V1 rules: Name + SCAC required; Preferred Chassis Provider required when Chassis Pool is
 * not GWI. (Holidays are intentionally not ported.)
 */
const TABLE = 'scacprofiles';

const s = (v: any): string => (v == null ? '' : String(v));
const yn = (v: any): 'Y' | 'N' => (s(v).toUpperCase() === 'Y' ? 'Y' : 'N');

interface OverrideRow {
  key: string;
  recno: number | null;   // null → staged-new
  region: string;
  preferredchassisprovider: string;
  chassispool: string;
  deleted: boolean;       // staged removal
}

const ScacProfileEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [opts, setOpts] = useState<ScacFormOptions | null>(null);
  const [form, setForm] = useState<TablemaintRow>({
    scac: '', name: '', perdiemcontact: '', perdiemphone: '', perdiemfax: '',
    perdiememail: '', comments: '', dotrace: 'N', isdown: 'N', cccsubscriber: 'N',
    preferredchassisprovider: '', chassispool: '',
  });
  const [formDeleted, setFormDeleted] = useState(false); // active inverted: true = inactive
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  const loadedOverrides = useRef<Map<number, ChassisOverride>>(new Map());
  const keyCounter = useRef(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getScacFormOptions().then(setOpts).catch(() => {});
    if (!isNew) {
      tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
      getChassisOverrides(recno).then((rows) => {
        loadedOverrides.current = new Map(rows.map((r) => [r.recno, r]));
        setOverrides(rows.map((r) => ({
          key: `db-${r.recno}`, recno: r.recno, region: s(r.region),
          preferredchassisprovider: s(r.preferredchassisprovider),
          chassispool: s(r.chassispool), deleted: false,
        })));
      }).catch(() => {});
    }
  }, [isNew, recno]);

  useEffect(() => {
    setForm({
      scac: s(record.scac), name: s(record.name), perdiemcontact: s(record.perdiemcontact),
      perdiemphone: s(record.perdiemphone), perdiemfax: s(record.perdiemfax),
      perdiememail: s(record.perdiememail), comments: s(record.comments),
      dotrace: yn(record.dotrace), isdown: yn(record.isdown), cccsubscriber: yn(record.cccsubscriber),
      preferredchassisprovider: s(record.preferredchassisprovider), chassispool: s(record.chassispool),
    });
    // Reversed soft-delete: active='Y' → not deleted.
    if (record.active !== undefined) setFormDeleted(s(record.active) !== 'Y');
  }, [record]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const providerOpts = useMemo(() => normalizeOptions(opts?.providers ?? []), [opts]);
  const poolOpts = useMemo(() => normalizeOptions(opts?.pools ?? []), [opts]);
  const regionOpts = useMemo(() => normalizeOptions(opts?.regions ?? []), [opts]);

  // V1 rule: Provider required unless Pool is GWI.
  const providerRequired = s(form.chassispool).trim().toUpperCase() !== 'GWI';

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!s(form.name).trim()) e.push('Name is required.');
    if (!s(form.scac).trim()) e.push('SCAC is required.');
    if (providerRequired && !s(form.preferredchassisprovider).trim())
      e.push('Preferred Chassis Provider is required when Chassis Pool is not GWI.');
    const badEmail = findInvalidEmails(s(form.perdiememail), { semicolon: true });
    if (badEmail.length) e.push(`Invalid Per Diem Email: ${badEmail.join(', ')}`);
    return e;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, providerRequired]);

  // Per-override validation: Region required, and Provider required unless Pool is GWI
  // (same rule as the parent). A fully-empty staged-new row is ignored (dropped on save).
  // Map override key → which field(s) are missing, for row/field highlighting.
  const overrideErrors = useMemo(() => {
    const map = new Map<string, { region: boolean; provider: boolean }>();
    overrides.forEach((o) => {
      if (o.deleted) return;
      const hasValue = !!(o.region || o.preferredchassisprovider || o.chassispool);
      if (o.recno === null && !hasValue) return; // empty new row → ignored
      const region = !o.region.trim();
      const provider = o.chassispool.trim().toUpperCase() !== 'GWI' && !o.preferredchassisprovider.trim();
      if (region || provider) map.set(o.key, { region, provider });
    });
    return map;
  }, [overrides]);

  const banner = [
    ...(showErrors ? errors : []),
    ...(showErrors && overrideErrors.size ? ['Each chassis override needs a Region, and a Provider unless its Pool is GWI.'] : []),
    ...(saveError ? [saveError] : []),
  ];

  // ── Chassis-override editing ────────────────────────────────────────────
  const addOverride = () => setOverrides((p) => [
    ...p,
    { key: `new-${keyCounter.current++}`, recno: null, region: '', preferredchassisprovider: '', chassispool: '', deleted: false },
  ]);
  const setOverrideField = (key: string, field: keyof OverrideRow, value: string) =>
    setOverrides((p) => p.map((o) => (o.key === key ? { ...o, [field]: value } : o)));
  const removeOverride = (key: string) => setOverrides((p) => p
    .map((o) => (o.key === key ? { ...o, deleted: true } : o))
    .filter((o) => !(o.recno === null && o.key === key)));  // drop staged-new outright

  const visibleOverrides = overrides.filter((o) => !o.deleted);

  const reconcileOverrides = async (parentRecno: number) => {
    for (const o of overrides) {
      const hasValue = o.region || o.preferredchassisprovider || o.chassispool;
      if (o.recno === null) {
        if (!o.deleted && hasValue) {
          await createChassisOverride(parentRecno, {
            region: o.region, preferredchassisprovider: o.preferredchassisprovider, chassispool: o.chassispool,
          });
        }
        continue;
      }
      const orig = loadedOverrides.current.get(o.recno);
      if (o.deleted) {
        await updateChassisOverride(o.recno, { deleted: 'Y' });
      } else if (orig && (orig.region !== o.region || orig.preferredchassisprovider !== o.preferredchassisprovider || orig.chassispool !== o.chassispool)) {
        await updateChassisOverride(o.recno, {
          region: o.region, preferredchassisprovider: o.preferredchassisprovider, chassispool: o.chassispool,
        });
      }
    }
  };

  const handleSave = useCallback(async () => {
    if (errors.length || overrideErrors.size) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    const payload: TablemaintRow = {
      scac: s(form.scac).trim(), name: s(form.name).trim(),
      perdiemcontact: s(form.perdiemcontact).trim(), perdiemphone: s(form.perdiemphone).trim(),
      perdiemfax: s(form.perdiemfax).trim(), perdiememail: s(form.perdiememail).trim(),
      comments: s(form.comments), dotrace: yn(form.dotrace), isdown: yn(form.isdown),
      cccsubscriber: yn(form.cccsubscriber),
      preferredchassisprovider: s(form.preferredchassisprovider), chassispool: s(form.chassispool),
    };
    try {
      let parentRecno = recno;
      if (isNew) {
        const created = await tablemaintService.create(TABLE, payload);
        parentRecno = Number(created.recno);
      } else {
        const patch: TablemaintRow = {};
        Object.keys(payload).forEach((k) => {
          if (s(form[k]) !== s(record[k])) patch[k] = payload[k];
        });
        // Reversed soft-delete: send `active` = 'Y' when the user marked it INACTIVE (the
        // engine maps 'Y'→deleted → stores active='N', 'N'→active → stores active='Y').
        if (formDeleted !== (s(record.active) !== 'Y')) patch.active = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length > 0) await tablemaintService.update(TABLE, recno, patch);
      }
      await reconcileOverrides(parentRecno);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save SCAC profile.');
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, overrideErrors, isNew, form, record, formDeleted, recno, overrides, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create SCAC' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add SCAC' : `Edit SCAC (#${recno})`;
  const billtoDisplay = s(record.billto);

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="60rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Identity */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">SCAC</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="SCAC" required value={s(form.scac)} onChange={(v) => setField('scac', v)}
              disabled={isSaving} maxLength={15} invalid={showErrors && !s(form.scac).trim()} />
            <TextField label="Name" required value={s(form.name)} onChange={(v) => setField('name', v)}
              disabled={isSaving} maxLength={255} invalid={showErrors && !s(form.name).trim()} />
          </div>
        </fieldset>

        {/* Per Diem contact */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Per Diem Contact</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Per Diem Contact" value={s(form.perdiemcontact)} onChange={(v) => setField('perdiemcontact', v)} disabled={isSaving} maxLength={100} />
            <TextField label="Per Diem Email" value={s(form.perdiememail)} onChange={(v) => setField('perdiememail', v)} disabled={isSaving} maxLength={100} />
            <TextField label="Per Diem Phone" value={s(form.perdiemphone)} onChange={(v) => setField('perdiemphone', v)} disabled={isSaving} maxLength={50} />
            <TextField label="Per Diem Fax" value={s(form.perdiemfax)} onChange={(v) => setField('perdiemfax', v)} disabled={isSaving} maxLength={50} />
          </div>
        </fieldset>

        {/* Chassis */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Chassis</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Preferred Chassis Provider" required={providerRequired}
              value={s(form.preferredchassisprovider)} onChange={(v) => setField('preferredchassisprovider', v)}
              options={providerOpts} disabled={isSaving}
              invalid={showErrors && providerRequired && !s(form.preferredchassisprovider).trim()} />
            <SelectField label="Chassis Pool" value={s(form.chassispool)} onChange={(v) => setField('chassispool', v)}
              options={poolOpts} disabled={isSaving} />
          </div>
          {!providerRequired && (
            <p className="mt-2 text-xs text-muted">Provider is optional when the pool is GWI.</p>
          )}
        </fieldset>

        {/* Chassis overrides subtable */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">
            Chassis Overrides <span className="text-xs font-normal text-gray-400">(per-region provider/pool)</span>
          </legend>
          {visibleOverrides.length === 0 && (
            <p className="text-sm text-muted mb-3">No overrides. Add one to specify a region-specific provider/pool.</p>
          )}
          {visibleOverrides.length > 0 && (
            <>
              {/* Column headers (shown once) */}
              <div className="hidden md:grid md:grid-cols-[1fr_1fr_1fr_auto] gap-3 px-2 pb-1">
                <span className="text-xs font-medium text-muted">Region <span className="text-red-500">*</span></span>
                <span className="text-xs font-medium text-muted">Provider</span>
                <span className="text-xs font-medium text-muted">Pool</span>
                <span className="w-9" />
              </div>
              <div className="space-y-2">
                {visibleOverrides.map((o) => {
                  const rowErr = showErrors ? overrideErrors.get(o.key) : undefined;
                  return (
                    <div key={o.key}>
                      <div className={`grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center ${rowErr ? 'rounded-lg border border-red-400 dark:border-red-500 bg-red-50/40 dark:bg-red-900/10 p-2' : ''}`}>
                        <div className={rowErr?.region ? 'rounded-lg ring-2 ring-red-400' : ''}>
                          <SelectField hideLabel label="Region" value={o.region} onChange={(v) => setOverrideField(o.key, 'region', v)} options={regionOpts} disabled={isSaving} />
                        </div>
                        <div className={rowErr?.provider ? 'rounded-lg ring-2 ring-red-400' : ''}>
                          <SelectField hideLabel label="Provider" value={o.preferredchassisprovider} onChange={(v) => setOverrideField(o.key, 'preferredchassisprovider', v)} options={providerOpts} disabled={isSaving} />
                        </div>
                        <SelectField hideLabel label="Pool" value={o.chassispool} onChange={(v) => setOverrideField(o.key, 'chassispool', v)} options={poolOpts} disabled={isSaving} />
                        <button type="button" onClick={() => removeOverride(o.key)} disabled={isSaving}
                          className="inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 p-2 text-gray-500 hover:text-red-600 hover:border-red-300 dark:hover:text-red-400 disabled:opacity-50"
                          aria-label="Remove override">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                      {rowErr && (
                        <p className="mt-1 ml-1 text-xs text-red-600 dark:text-red-400">
                          {[rowErr.region ? 'Region is required.' : '', rowErr.provider ? 'Provider is required unless Pool is GWI.' : ''].filter(Boolean).join(' ')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <button type="button" onClick={addOverride} disabled={isSaving}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50">
            <PlusIcon className="w-4 h-4" /> Add Override
          </button>
        </fieldset>

        {/* Comments */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Comments</legend>
          <TextAreaField label="Comments" value={s(form.comments)} onChange={(v) => setField('comments', v)} disabled={isSaving} />
        </fieldset>

        {/* Options + trace status */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
          <legend className="px-2 text-sm font-semibold text-label">Options</legend>
          <Toggle label="Trace?" checked={s(form.dotrace) === 'Y'} disabled={isSaving} onChange={(v) => setField('dotrace', v ? 'Y' : 'N')} />
          <Toggle label="Down?" checked={s(form.isdown) === 'Y'} disabled={isSaving} onChange={(v) => setField('isdown', v ? 'Y' : 'N')} />
          <Toggle label="CCC Subscriber" checked={s(form.cccsubscriber) === 'Y'} disabled={isSaving} onChange={(v) => setField('cccsubscriber', v ? 'Y' : 'N')} />
        </fieldset>

        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted mb-3">Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="flex flex-col md:col-span-2">
                <span className="text-xs font-medium text-muted">Bill To</span>
                {billtoDisplay
                  ? billtoDisplay.split('\n').map((line, i) => (
                      <span key={i} className="text-gray-800 dark:text-gray-200 break-words">{line}</span>
                    ))
                  : <span className="text-gray-800 dark:text-gray-200">—</span>}
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted">Last Attempted</span>
                <span className="text-gray-800 dark:text-gray-200">{s(record.lastattempteddatetime) || '—'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted">Last Successful</span>
                <span className="text-gray-800 dark:text-gray-200">{s(record.lastsuccessfuldatetime) || '—'}</span>
              </div>
            </div>
          </div>
        )}

        {!isNew && (
          <div className={`rounded-lg border p-3 ${formDeleted ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
            <Toggle label="Active" checked={!formDeleted} disabled={isSaving} onChange={(v) => setFormDeleted(!v)}
              hint="Inactive SCACs are hidden from filters but preserved. Uncheck to deactivate." />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ScacProfileEditDrawer;
